// src/authpages/dashboard/StudyStatsService.js
import { 
  auth, 
  db, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  addDoc,
  increment,
  serverTimestamp,
  getDocs,
  query,
  where
} from '../../firebase';

/**
 * Helper function to get the start of the current week (Monday)
 * @returns {string} Start date of current week in YYYY-MM-DD format
 */
const getStartOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() - mondayOffset);
  monday.setHours(0, 0, 0, 0);
  
  return monday.toISOString().split('T')[0];
};

/**
 * Enhanced version of getUserStudyStats that handles weekly data management
 * @returns {Promise<Object>} Study stats data
 */
export const getUserStudyStats = async () => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      console.error('No user is signed in');
      return null;
    }
    
    console.log('Fetching study stats for user:', user.uid);
    
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    // Default stats if user document doesn't exist
    if (!userDoc.exists()) {
      console.log('No existing user document, creating default');
      
      const defaultStats = {
        totalStudyTime: 0,
        completedSessions: 0,
        weeklyAverage: 0,
        studyDays: {},
        currentWeekStart: getStartOfWeek(),
        weeklyHistory: [],
        lastResetDate: new Date().toISOString().split('T')[0],
        lastUpdated: serverTimestamp()
      };
      
      // Create new user document
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        studyStats: defaultStats,
        createdAt: serverTimestamp()
      });
      
      return defaultStats;
    }
    
    // Get user data and extract study stats
    const userData = userDoc.data();
    let statsData = userData.studyStats || {
      totalStudyTime: 0,
      completedSessions: 0,
      weeklyAverage: 0,
      studyDays: {},
      currentWeekStart: getStartOfWeek(),
      weeklyHistory: [],
      lastResetDate: new Date().toISOString().split('T')[0],
      lastUpdated: serverTimestamp()
    };
    
    // Ensure data types are correct
    if (typeof statsData.totalStudyTime !== 'number') {
      console.warn('totalStudyTime is not a number, converting:', statsData.totalStudyTime);
      statsData.totalStudyTime = parseInt(statsData.totalStudyTime || '0', 10);
    }
    
    if (typeof statsData.completedSessions !== 'number') {
      console.warn('completedSessions is not a number, converting:', statsData.completedSessions);
      statsData.completedSessions = parseInt(statsData.completedSessions || '0', 10);
    }
    
    // Validate and fix studyDays if needed
    if (!statsData.studyDays) {
      statsData.studyDays = {};
    } else {
      // Ensure all studyDays values are numbers
      Object.entries(statsData.studyDays).forEach(([date, minutes]) => {
        if (typeof minutes !== 'number') {
          console.warn(`studyDays[${date}] is not a number, converting:`, minutes);
          statsData.studyDays[date] = parseInt(minutes || '0', 10);
        }
      });
    }
    
    // Initialize weeklyHistory if it doesn't exist
    if (!statsData.weeklyHistory) {
      statsData.weeklyHistory = [];
    }
    
    // Initialize currentWeekStart if it doesn't exist
    if (!statsData.currentWeekStart) {
      statsData.currentWeekStart = getStartOfWeek();
    }
    
    const today = new Date().toISOString().split('T')[0];
    const currentWeekStart = getStartOfWeek();
    
    // Check if we're in a new week
    if (statsData.currentWeekStart !== currentWeekStart) {
      console.log('New week detected, archiving previous week data');
      
      // Calculate start and end dates for the previous week
      const prevWeekStart = new Date(statsData.currentWeekStart);
      const prevWeekEnd = new Date(currentWeekStart);
      prevWeekEnd.setDate(prevWeekEnd.getDate() - 1);
      
      const prevWeekStartStr = prevWeekStart.toISOString().split('T')[0];
      const prevWeekEndStr = prevWeekEnd.toISOString().split('T')[0];
      
      // Extract just the previous week's data
      const prevWeekData = {};
      let totalMinutesForWeek = 0;
      
      Object.entries(statsData.studyDays || {}).forEach(([date, minutes]) => {
        if (date >= prevWeekStartStr && date <= prevWeekEndStr) {
          prevWeekData[date] = minutes;
          totalMinutesForWeek += minutes;
        }
      });
      
      // Create a summary of the week's activity
      const weekSummary = {
        weekStart: prevWeekStartStr,
        weekEnd: prevWeekEndStr,
        totalMinutes: totalMinutesForWeek,
        dailyData: prevWeekData,
        averageMinutesPerDay: Math.round(totalMinutesForWeek / 7)
      };
      
      // Update weekly history (keeping last 10 weeks)
      const updatedHistory = [weekSummary, ...(statsData.weeklyHistory || [])].slice(0, 10);
      
      // Calculate new weekly average based on the last 4 weeks
      const recentWeeks = updatedHistory.slice(0, 4);
      const totalMinutesRecent = recentWeeks.reduce((sum, week) => sum + week.totalMinutes, 0);
      const weeklyAverage = Math.round(totalMinutesRecent / (recentWeeks.length * 7));
      
      console.log('Updating week data:', {
        weeklyAverage,
        currentWeekStart,
        prevWeekSummary: weekSummary
      });
      
      // Store the updated data
      await updateDoc(userRef, {
        'studyStats.weeklyHistory': updatedHistory,
        'studyStats.currentWeekStart': currentWeekStart,
        'studyStats.weeklyAverage': weeklyAverage,
        'studyStats.lastResetDate': today,
        'studyStats.lastUpdated': serverTimestamp()
      });
      
      // Update local data
      statsData.weeklyHistory = updatedHistory;
      statsData.currentWeekStart = currentWeekStart;
      statsData.weeklyAverage = weeklyAverage;
      statsData.lastResetDate = today;
    }
    
    console.log('Retrieved stats data:', {
      totalStudyTime: statsData.totalStudyTime,
      completedSessions: statsData.completedSessions,
      weeklyAverage: statsData.weeklyAverage,
      weeklyHistoryCount: statsData.weeklyHistory?.length || 0
    });
    
    return statsData;
  } catch (error) {
    console.error('Error retrieving study stats:', error);
    console.error('Error details:', error.code, error.message);
    return null;
  }
};

/**
 * Record a completed study session with enhanced rewards tracking
 * @param {Object} sessionData - Data about the completed study session
 * @param {number} sessionData.minutes - Duration of the study session in minutes
 * @param {string} sessionData.sessionType - Type of study session (e.g., 'focus', 'review')
 * @param {boolean} sessionData.hadBreaks - Whether the session had breaks
 * @param {boolean} sessionData.quizCompleted - Whether a quiz was completed
 * @param {number} sessionData.quizDurationSeconds - Time taken to complete the quiz
 * @param {Array<string>} sessionData.studyPartners - IDs of study partners
 * @param {string} sessionData.topicId - ID of the topic studied
 * @param {string} sessionData.notes - Any notes about the session
 * @returns {Promise<boolean>} Success status
 */
export const recordStudySession = async (sessionData) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      console.error('No user is signed in');
      return false;
    }
    
    // Extract or set default values
    const minutes = sessionData.minutes || 0;
    const sessionType = sessionData.sessionType || 'focus';
    const hadBreaks = sessionData.hadBreaks || false;
    const quizCompleted = sessionData.quizCompleted || false;
    const quizDurationSeconds = sessionData.quizDurationSeconds || 0;
    const studyPartners = sessionData.studyPartners || [];
    const topicId = sessionData.topicId || '';
    const notes = sessionData.notes || '';
    
    // Ensure minutes is a number
    const minutesNum = typeof minutes === 'number' ? minutes : parseInt(minutes, 10);
    if (isNaN(minutesNum) || minutesNum <= 0) {
      console.error('Invalid minutes value:', minutes);
      return false;
    }
    
    console.log(`Recording study session: ${minutesNum} minutes, type: ${sessionType}`);
    
    // Get the current date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    const now = new Date();
    
    // Prepare reward tracking data
    const rewardSessionData = {
      durationMinutes: minutesNum,
      completedAt: now.toISOString(),
      hadBreaks: hadBreaks,
      quizCompleted: quizCompleted,
      quizDurationSeconds: quizDurationSeconds,
      studyPartners: studyPartners,
      topicId: topicId,
      notes: notes
    };
    
    // Update user document with study stats
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Update existing document
      await updateDoc(userRef, {
        'studyStats.totalStudyTime': increment(minutesNum),
        'studyStats.completedSessions': increment(1),
        [`studyStats.studyDays.${today}`]: increment(minutesNum),
        'studyStats.lastUpdated': serverTimestamp()
      });
      
      console.log('Stats document updated successfully');
    } else {
      // Create new user document with initial stats
      const initialStudyStats = {
        totalStudyTime: minutesNum,
        completedSessions: 1,
        studyDays: { [today]: minutesNum },
        weeklyAverage: Math.round(minutesNum / 7),
        lastUpdated: serverTimestamp(),
        lastResetDate: today
      };
      
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        studyStats: initialStudyStats,
        createdAt: serverTimestamp()
      });
      
      console.log('New user document created with study stats');
    }
    
    // Add to the studySessions collection for detailed history
    const sessionRecord = {
      userId: user.uid,
      minutes: minutesNum,
      sessionType,
      date: today,
      timestamp: serverTimestamp(),
      hadBreaks,
      quizCompleted,
      quizDurationSeconds,
      studyPartners,
      topicId,
      notes
    };
    
    const sessionsRef = collection(db, 'studySessions');
    const newSessionRef = await addDoc(sessionsRef, sessionRecord);
    console.log('Study session recorded with ID:', newSessionRef.id);
    
    // Update rewards data
    await updateRewards(user.uid, rewardSessionData);

    // Force refresh the dashboard
    forceDashboardRefresh();
    
    return true;
  } catch (error) {
    console.error('Error recording study session:', error);
    console.error('Error details:', error.code, error.message);
    return false;
  }
};

/**
 * Update user rewards based on completed session
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session data for rewards calculation
 * @returns {Promise<void>}
 */
const updateRewards = async (userId, sessionData) => {
  try {
    // Check if the global tracking function is available
    if (typeof window !== 'undefined' && window.trackStudySession) {
      console.log('Using global rewards tracking function');
      await window.trackStudySession(sessionData);
      return;
    }
    
    // Fallback implementation if global function is not available
    console.log('Global tracking function not available, using fallback implementation');
    
    // Get or create rewards document
    const userRewardsRef = doc(db, 'userRewards', userId);
    const userRewardsDoc = await getDoc(userRewardsRef);
    
    const badgeDefinitions = [
      { id: 1, name: "Early Bird", requirementType: "earlyMorningCount", requirementValue: 5 },
      { id: 2, name: "Bookworm", requirementType: "totalHours", requirementValue: 20 },
      { id: 3, name: "Focus Master", requirementType: "longSessionWithoutBreak", requirementValue: 1 },
      { id: 4, name: "Social Learner", requirementType: "uniqueStudyPartners", requirementValue: 3 },
      { id: 5, name: "Speed Demon", requirementType: "quickQuizzesCompleted", requirementValue: 10 },
      { id: 6, name: "Weekend Warrior", requirementType: "weekendStudyHours", requirementValue: 5 },
    ];
    
    const completedAt = new Date(sessionData.completedAt);
    
    if (userRewardsDoc.exists()) {
      // Update existing rewards data
      const userData = userRewardsDoc.data();
      let updatedStats = { ...userData.studyStats } || {
        totalHours: 0,
        sessionsCompleted: 0,
        earlyMorningCount: 0,
        longSessionWithoutBreak: 0,
        uniqueStudyPartners: 0,
        quickQuizzesCompleted: 0,
        weekendStudyHours: 0
      };
      
      let earnedXp = 0;
      
      // Update study stats based on session
      if (sessionData.durationMinutes) {
        // Add XP for study minutes (10 XP per minute)
        earnedXp += sessionData.durationMinutes * 10;
        
        // Update total hours
        const hoursStudied = sessionData.durationMinutes / 60;
        updatedStats.totalHours = (updatedStats.totalHours || 0) + hoursStudied;
        
        // Check if session was completed before 9 AM
        if (completedAt.getHours() < 9) {
          updatedStats.earlyMorningCount = (updatedStats.earlyMorningCount || 0) + 1;
        }
        
        // Check if session was on a weekend
        const day = completedAt.getDay();
        if (day === 0 || day === 6) { // 0 = Sunday, 6 = Saturday
          updatedStats.weekendStudyHours = (updatedStats.weekendStudyHours || 0) + hoursStudied;
        }
        
        // Check if this was a long session without breaks
        if (sessionData.durationMinutes >= 120 && !sessionData.hadBreaks) {
          updatedStats.longSessionWithoutBreak = (updatedStats.longSessionWithoutBreak || 0) + 1;
        }
        
        // Track completed session
        updatedStats.sessionsCompleted = (updatedStats.sessionsCompleted || 0) + 1;
      }
      
      // Check if user completed a quiz
      if (sessionData.quizCompleted && sessionData.quizDurationSeconds < 180) {
        // Quiz completed in less than 3 minutes
        updatedStats.quickQuizzesCompleted = (updatedStats.quickQuizzesCompleted || 0) + 1;
      }
      
      // Track study partners if applicable
      if (sessionData.studyPartners && sessionData.studyPartners.length > 0) {
        // Get existing partners
        const existingPartners = userData.studyPartners || [];
        
        // Add new unique partners
        const newPartners = sessionData.studyPartners.filter(
          partner => !existingPartners.includes(partner)
        );
        
        if (newPartners.length > 0) {
          const allPartners = [...existingPartners, ...newPartners];
          
          // Update unique partners count
          updatedStats.uniqueStudyPartners = allPartners.length;
          
          // Update the partners list
          await updateDoc(userRewardsRef, {
            studyPartners: allPartners
          });
        }
      }
      
      // Check and award new badges
      const updatedBadges = [...(userData.badges || [])];
      
      // Make sure all badge definitions exist in the user's badges
      badgeDefinitions.forEach(badgeDef => {
        const existingBadge = updatedBadges.find(b => b.id === badgeDef.id);
        if (!existingBadge) {
          updatedBadges.push({
            id: badgeDef.id,
            name: badgeDef.name,
            earned: false
          });
        }
      });
      
      badgeDefinitions.forEach(badgeDef => {
        const userBadge = updatedBadges.find(b => b.id === badgeDef.id);
        
        // Skip already earned badges
        if (userBadge && !userBadge.earned) {
          const requirementMet = updatedStats[badgeDef.requirementType] >= badgeDef.requirementValue;
          
          if (requirementMet) {
            userBadge.earned = true;
            userBadge.earnedAt = new Date().toISOString();
            earnedXp += 500; // Award 500 XP for each new badge
          }
        }
      });
      
      // Calculate new XP and check for level up
      let newXp = (userData.xp || 0) + earnedXp;
      let newLevel = userData.level || 1;
      let newXpToNextLevel = userData.xpToNextLevel || 1000;
      
      // Level up if needed
      while (newXp >= newXpToNextLevel) {
        newXp -= newXpToNextLevel;
        newLevel++;
        newXpToNextLevel = Math.round(1000 * Math.pow(1.25, newLevel - 1));
      }
      
      // Update streak
      let streak = userData.streak || 0;
      const lastStudyDate = userData.lastStudyDate || '';
      const todayStr = new Date().toISOString().split('T')[0];
      
      if (lastStudyDate === todayStr) {
        // Already studied today, no streak update
      } else if (!lastStudyDate || isYesterday(lastStudyDate)) {
        // First study session or consecutive day
        streak += 1;
      } else {
        // Streak broken
        streak = 1;
      }
      
      // Update the user rewards document
      await updateDoc(userRewardsRef, {
        xp: newXp,
        level: newLevel,
        xpToNextLevel: newXpToNextLevel,
        studyStats: updatedStats,
        badges: updatedBadges,
        streak: streak,
        lastStudyDate: todayStr,
        lastUpdated: serverTimestamp()
      });
      
    } else {
      // Create new rewards document with initial values
      const initialBadges = badgeDefinitions.map(badge => ({
        id: badge.id,
        name: badge.name,
        earned: false
      }));
      
      // Calculate initial earned XP (10 XP per minute)
      const earnedXp = sessionData.durationMinutes * 10;
      
      // Calculate initial stats
      const initialStats = {
        totalHours: sessionData.durationMinutes / 60,
        sessionsCompleted: 1,
        earlyMorningCount: completedAt.getHours() < 9 ? 1 : 0,
        longSessionWithoutBreak: (!sessionData.hadBreaks && sessionData.durationMinutes >= 120) ? 1 : 0,
        uniqueStudyPartners: sessionData.studyPartners?.length || 0,
        quickQuizzesCompleted: (sessionData.quizCompleted && sessionData.quizDurationSeconds < 180) ? 1 : 0,
        weekendStudyHours: (completedAt.getDay() === 0 || completedAt.getDay() === 6) ? sessionData.durationMinutes / 60 : 0
      };
      
      await setDoc(userRewardsRef, {
        userId,
        level: 1,
        xp: earnedXp,
        xpToNextLevel: 1000,
        streak: 1,
        badges: initialBadges,
        studyStats: initialStats,
        studyPartners: sessionData.studyPartners || [],
        lastStudyDate: new Date().toISOString().split('T')[0],
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    }
    
  } catch (error) {
    console.error('Error updating rewards:', error);
  }
};

/**
 * Helper function to check if a date string is yesterday
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if the date is yesterday
 */
const isYesterday = (dateStr) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  return dateStr === yesterdayStr;
};

/**
 * Register the rewards tracking function globally
 * @param {Function} trackFunction - Function to track rewards
 */
export const registerRewardsTrackingFunction = (trackFunction) => {
  if (typeof window !== 'undefined') {
    window.trackStudySession = trackFunction;
    console.log('Rewards tracking function registered');
  }
};

/**
 * Force a dashboard refresh by dispatching a refresh event
 * @returns {boolean} Success status
 */
export const forceDashboardRefresh = () => {
  const refreshEvent = new Event('refreshStudyStats');
  window.dispatchEvent(refreshEvent);
  console.log("Forced dashboard refresh triggered");
  return true;
};

// Export recordStudySession as a named export recordStudySessionWithRewards
export { recordStudySession as recordStudySessionWithRewards };

// Default export for backward compatibility
export default {
  getUserStudyStats,
  recordStudySession,
  recordStudySessionWithRewards: recordStudySession,
  registerRewardsTrackingFunction
};